/* eslint-disable no-use-before-define */
import { Group, Skeleton } from '@mantine/core';
import { sanitizeUrl } from '@repo/common/utils/urls';
import { Icon, Text } from '@repo/foundations';
import type { MouseEvent, SyntheticEvent } from 'react';
import { memo, useRef, useState } from 'react';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import styled from 'styled-components';
import type { SecodaEditorComponentProps } from '../../types';
import { ResizeHandle } from './ResizeHandle';
import { useDragResize } from './useDragResize';

type Props = SecodaEditorComponentProps & {
	/** Callback triggered when the image is clicked */
	onClick: (event: MouseEvent<HTMLDivElement>) => void;
	/** Callback triggered when the download button is clicked */
	// onDownload?: (event: MouseEvent<HTMLElement>) => void;
	/** Callback triggered when the image is resized */
	onChangeWidth: (width: number) => void;
	/** The editor view */
	children?: React.ReactElement;
};

export const ImageContainerV2 = memo(
	({
		children,
		isSelected,
		node,
		isEditable,
		onChangeWidth,
		onClick,
	}: Props) => {
		const { src, layoutClass } = node.attrs;
		const [loaded, setLoaded] = useState(false);
		const [error, setError] = useState(false);
		const [naturalWidth, setNaturalWidth] = useState(node.attrs.width);
		const containerRef = useRef<HTMLDivElement>(null);
		const resizeRef = useRef<HTMLDivElement>(null);

		const isResizable = isEditable && !error;

		const { handlePointerDown, dragging, resetImage, maxWidth } = useDragResize(
			{
				initialWidth: node.attrs.width ?? naturalWidth,
				naturalWidth,
				onChangeWidth,
				containerRef,
				resizeRef,
				isResizable,
				shouldExpandBilaterally:
					layoutClass !== 'left-50' && layoutClass !== 'right-50',
			}
		);

		const sanitizedSrc = sanitizeUrl(src);

		const handleClick = (event: MouseEvent<HTMLDivElement>) => {
			if (dragging) {
				return;
			}
			onClick(event);
		};

		return (
			<div
				contentEditable={false}
				className={layoutClass ? `image image-${layoutClass}` : 'image'}
				ref={containerRef}
			>
				<ImageWrapper
					ref={resizeRef}
					className={isSelected || dragging ? 'ProseMirror-selectednode' : ''}
					onClick={handleClick}
					style={{ width: node.attrs.width ?? 'auto' }}
					data-dragging={dragging}
				>
					{error ? (
						<Error
							style={{
								width: `${node.attrs.width ?? 300}px`,
								height: `${node.attrs.width ?? 300}px`,
							}}
							className="image-handle"
						>
							<Group spacing="2xs">
								<Icon name="circleXFilled" />
								<Text size="sm">Image failed to load</Text>
							</Group>
						</Error>
					) : (
						<Zoom>
							<img
								className="image-handle"
								style={{
									width: '100%',
									maxWidth: maxWidth,
									display: loaded ? 'block' : 'none',
									pointerEvents:
										dragging || (!isSelected && isEditable) ? 'none' : 'all',
								}}
								src={sanitizedSrc}
								onError={() => {
									setError(true);
									setLoaded(true);
								}}
								onLoad={(ev: SyntheticEvent<HTMLImageElement>) => {
									// For some SVG's Firefox does not provide the naturalWidth, in this
									// rare case we need to provide a default so that the image can be
									// seen and is not sized to 0px
									const nw =
										(ev.target as HTMLImageElement).naturalWidth || 300;
									setNaturalWidth(nw);
									setLoaded(true);

									if (!node.attrs.width) {
										resetImage(nw);
									}
								}}
							/>
							{!loaded && node.attrs.width && (
								<Skeleton
									width={node.attrs.width ?? 300}
									height={node.attrs.width ?? 300}
								/>
							)}
						</Zoom>
					)}
					{isResizable && (
						<>
							<ResizeHandle
								direction="left"
								onPointerDown={handlePointerDown('left')}
								dragging={!!dragging}
							/>
							<ResizeHandle
								direction="right"
								onPointerDown={handlePointerDown('right')}
								dragging={!!dragging}
							/>
						</>
					)}
				</ImageWrapper>
				{children}
			</div>
		);
	}
);

function getPlaceholder(width: number, height: number) {
	return `<svg xmlns="http://www.w3.org/2000/svg" width="${width ?? 300}" height="${height ?? 300}" />`;
}

function Error({
	children,
	style,
	className,
}: {
	children: React.ReactNode;
	style: React.CSSProperties;
	className: string;
}) {
	return (
		<Group
			sx={(theme) => ({
				maxWidth: '100%',
				color: theme.other.getColor('text/secondary/default'),
				fontSize: theme.fontSizes.xs,
				backgroundColor: theme.other.getColor('fill/secondary/default'),
				borderRadius: theme.radius.xs,
				height: 80,
				alignItems: 'center',
				justifyContent: 'center',
				userSelect: 'none',
			})}
			style={style}
			className={className}
		>
			{children}
		</Group>
	);
}

const ImageWrapper = styled.div`
	line-height: 0;
	position: relative;
	margin-left: auto;
	margin-right: auto;
	max-width: 100%;
	overflow: hidden;

	&:hover {
		.image-actions {
			opacity: 0.9;
		}

		.resize-handle {
			opacity: 1;
		}
	}
`;
